import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { fetchProjectCreationSettings, saveProjectCreationSettings } from '@/lib/data-service';
import {
  PROJECT_ROLE_DISPLAY_ORDER,
  PROJECT_ROLE_LABELS,
  type ProjectRoleType,
  type ProjectRoleConfig,
} from '@/types';

// Field configuration types
export type ProjectFieldKey = 
  | 'projectName'
  | 'projectCode'
  | 'description'
  | 'status'
  | 'priority'
  | 'type'
  | 'startDate'
  | 'endDate'
  | 'duration'
  | 'people'
  | 'clients'
  | 'costCentres'
  | 'currencyCode'
  | 'budget'
  | 'customAttributes'
  | 'objectives'
  | 'keyDeliverables';



/** 3-state field visibility/requirement control */
export type FieldState = 'hidden' | 'optional' | 'required';

/** Project code generation mode */
export type ProjectCodeMode = 'automatic' | 'manual' | 'auto-override';

/** Number format for auto-generated project codes */
export type ProjectCodeNumberFormat = '001' | '0001' | '00001';

export interface ProjectCodeSettings {
  mode: ProjectCodeMode;
  prefix: string;
  numberFormat: ProjectCodeNumberFormat;
  nextSequence: number; // Used to preview the example output
}

/** Re-export role types so consumers can import from this context file */
export type { ProjectRoleType, ProjectRoleConfig };

/** 3-state for individual stakeholder roles */
export type RoleState = 'hidden' | 'optional' | 'required';

export interface FieldConfig {
  key: ProjectFieldKey;
  label: string;
  /** 3-state: hidden | optional | required */
  fieldState: FieldState;
  /** Derived from fieldState — kept for backward compatibility */
  isRequired: boolean;
  isAlwaysRequired?: boolean; // Cannot be changed by user (projectName, projectCode)
  section: 'essential' | 'general' | 'timeline' | 'management';
  order: number;
  autoGenerateCode?: boolean; // For projectCode field - auto-generate from project name
}

/** Derive isRequired from fieldState */
function deriveIsRequired(fieldState: FieldState): boolean {
  return fieldState === 'required';
}

/** Migrate a legacy boolean isRequired to the new fieldState */
function migrateToFieldState(isRequired: boolean, isHidden?: boolean): FieldState {
  if (isHidden) return 'hidden';
  return isRequired ? 'required' : 'optional';
}

export interface TimelineSettings {

  timelineCompletionRequired: boolean; // true = user must fill Start Date + (End Date OR Duration)
}

interface FieldConfigContextType {
  fieldConfigs: FieldConfig[];
  requiredFields: ProjectFieldKey[];
  timelineSettings: TimelineSettings;
  defaultStatusId: number | null;
  projectCodeSettings: ProjectCodeSettings;
  updateFieldConfig: (key: ProjectFieldKey, updates: Partial<FieldConfig>) => void;
  /** Convenience method to set the 3-state field state */
  updateFieldState: (key: ProjectFieldKey, state: FieldState) => void;
  updateTimelineSettings: (updates: Partial<TimelineSettings>) => void;
  updateDefaultStatusId: (id: number | null) => void;
  updateProjectCodeSettings: (updates: Partial<ProjectCodeSettings>) => void;
  /** Stakeholder role configurations (Hidden / Optional / Required per role) */
  roleConfigs: ProjectRoleConfig[];
  updateRoleState: (role: ProjectRoleType, state: RoleState) => void;
  getRequiredFieldsBySection: (section: FieldConfig['section']) => FieldConfig[];
  getFieldConfig: (key: ProjectFieldKey) => FieldConfig | undefined;
  generateProjectCode: (projectName: string, maxLength?: number) => string;
  /** Generate an example project code from current settings */
  generateExampleCode: () => string;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  isSettingsLoadError: boolean;
  saveChanges: () => Promise<void>;
  resetChanges: () => void;
  resetToDefaults: () => void;
  makeAllRequired: () => void;
  makeAllOptional: () => void;
  makeAllHidden: () => void;
}

const FieldConfigContext = createContext<FieldConfigContextType | undefined>(undefined);

const STORAGE_KEY = 'projectFieldConfigs';
const TIMELINE_STORAGE_KEY = 'projectTimelineSettings';
const DEFAULT_STATUS_STORAGE_KEY = 'projectCreationDefaultStatusId';
const PROJECT_CODE_SETTINGS_KEY = 'projectCodeSettings';
const ROLE_CONFIGS_STORAGE_KEY = 'projectCreationRoleConfigs';

/** Derive a ProjectRoleConfig from a RoleState */
function roleStateToConfig(role: ProjectRoleType, state: RoleState): ProjectRoleConfig {
  return {
    role,
    enabled: state !== 'hidden',
    required: state === 'required',
  };
}

/** Derive a RoleState from a ProjectRoleConfig */
function configToRoleState(config: ProjectRoleConfig): RoleState {
  if (!config.enabled) return 'hidden';
  if (config.required) return 'required';
  return 'optional';
}

/** Default role configs — PM always required, all others optional */
const DEFAULT_ROLE_CONFIGS: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => ({
  role,
  enabled: true,
  required: role === 'project-manager',
  displayLabel: undefined,
}));

function parseRoleConfigs(saved: string | null): ProjectRoleConfig[] {
  if (saved) {
    try {
      const parsed: ProjectRoleConfig[] = JSON.parse(saved);
      // Ensure all roles are present (in case new roles are added later)
      return PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
        const found = parsed.find((c) => c.role === role);
        return found ?? { role, enabled: true, required: role === 'project-manager' };
      });
    } catch { /* fall through */ }
  }
  return DEFAULT_ROLE_CONFIGS;
}

const DEFAULT_PROJECT_CODE_SETTINGS: ProjectCodeSettings = {
  mode: 'manual',
  prefix: 'PROJ-',
  numberFormat: '001',
  nextSequence: 42,
};

// Default field configurations
const DEFAULT_FIELD_CONFIGS: FieldConfig[] = [
  // Essential Fields (Always Required — locked)
  { key: 'projectName', label: 'Project Name', fieldState: 'required', isRequired: true, isAlwaysRequired: true, section: 'essential', order: 1 },
  { key: 'projectCode', label: 'Project Code', fieldState: 'required', isRequired: true, isAlwaysRequired: true, section: 'essential', order: 2 },
  
  // General Information
  { key: 'description', label: 'Description', fieldState: 'optional', isRequired: false, section: 'general', order: 3 },
  { key: 'status', label: 'Status', fieldState: 'optional', isRequired: false, section: 'general', order: 4 },
  { key: 'priority', label: 'Priority', fieldState: 'optional', isRequired: false, section: 'general', order: 5 },
  { key: 'type', label: 'Type', fieldState: 'optional', isRequired: false, section: 'general', order: 6 },
  
  // Timeline & Scheduling
  { key: 'startDate', label: 'Start Date', fieldState: 'optional', isRequired: false, section: 'timeline', order: 8 },
  { key: 'endDate', label: 'End Date', fieldState: 'optional', isRequired: false, section: 'timeline', order: 9 },
  { key: 'duration', label: 'Duration', fieldState: 'optional', isRequired: false, section: 'timeline', order: 10 },
  
  // Management & Resources
  { key: 'people', label: 'Key People', fieldState: 'optional', isRequired: false, section: 'management', order: 11 },
  { key: 'clients', label: 'Clients', fieldState: 'optional', isRequired: false, section: 'management', order: 12 },
  { key: 'costCentres', label: 'Cost Centres', fieldState: 'optional', isRequired: false, section: 'management', order: 13 },
  { key: 'currencyCode', label: 'Currency Code', fieldState: 'optional', isRequired: false, section: 'management', order: 15 },
  { key: 'budget', label: 'Budget', fieldState: 'optional', isRequired: false, section: 'management', order: 16 },
  { key: 'customAttributes', label: 'Project Attributes', fieldState: 'optional', isRequired: false, section: 'management', order: 17 },
  { key: 'objectives', label: 'Objectives', fieldState: 'optional', isRequired: false, section: 'general', order: 18 },
  { key: 'keyDeliverables', label: 'Key Deliverables', fieldState: 'optional', isRequired: false, section: 'general', order: 19 },
];

// Default timeline settings
const DEFAULT_TIMELINE_SETTINGS: TimelineSettings = {

  timelineCompletionRequired: false, // Timeline completion is optional by default
};

export function FieldConfigProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingsLoadError, setIsSettingsLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasFetchedFromApi = useRef(false);

  // Helper function to parse field configs from localStorage or API
  const parseFieldConfigs = (saved: string | null): FieldConfig[] => {
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Migrate old isMandatory to isRequired, and old people keys to 'people'
        const OLD_PEOPLE_KEYS = new Set(['projectOwner', 'projectManager', 'projectManagers']);
        const migrated = parsed
          .filter((config: FieldConfig & { key: string }) => !OLD_PEOPLE_KEYS.has(config.key))
          .map((config: FieldConfig & { isMandatory?: boolean; isAlwaysMandatory?: boolean; isHidden?: boolean }) => {
            const isRequired = config.isRequired ?? config.isMandatory ?? false;
            const isAlwaysRequired = config.isAlwaysRequired ?? config.isAlwaysMandatory ?? false;
            // Migrate to 3-state fieldState if not already present
            const fieldState: FieldState = config.fieldState ?? migrateToFieldState(isRequired, config.isHidden);
            return {
              ...config,
              fieldState,
              isRequired: deriveIsRequired(fieldState),
              isAlwaysRequired,
            };
          });
        // Ensure 'people' entry exists
        if (!migrated.find((c: FieldConfig) => c.key === 'people')) {
          migrated.splice(10, 0, { key: 'people', label: 'Key People', fieldState: 'optional' as FieldState, isRequired: false, section: 'management', order: 11 });
        }
        return migrated;
      } catch {
        return DEFAULT_FIELD_CONFIGS;
      }
    }
    return DEFAULT_FIELD_CONFIGS;
  };

  // Helper function to parse timeline settings from localStorage or API
  const parseTimelineSettings = (saved: string | null): TimelineSettings => {
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_TIMELINE_SETTINGS;
      }
    }
    return DEFAULT_TIMELINE_SETTINGS;
  };

  // Load saved configs from localStorage initially
  const [savedFieldConfigs, setSavedFieldConfigs] = useState<FieldConfig[]>(() => {
    return parseFieldConfigs(localStorage.getItem(STORAGE_KEY));
  });

  const [savedTimelineSettings, setSavedTimelineSettings] = useState<TimelineSettings>(() => {
    return parseTimelineSettings(localStorage.getItem(TIMELINE_STORAGE_KEY));
  });

  // Default status ID state
  const [savedDefaultStatusId, setSavedDefaultStatusId] = useState<number | null>(() => {
    const stored = localStorage.getItem(DEFAULT_STATUS_STORAGE_KEY);
    return stored ? Number(stored) : 2; // Default to "In Progress" (id: 2)
  });

  // Project code settings state
  const [savedProjectCodeSettings, setSavedProjectCodeSettings] = useState<ProjectCodeSettings>(() => {
    const stored = localStorage.getItem(PROJECT_CODE_SETTINGS_KEY);
    if (stored) {
      try { return { ...DEFAULT_PROJECT_CODE_SETTINGS, ...JSON.parse(stored) }; } catch { /* fall through */ }
    }
    return DEFAULT_PROJECT_CODE_SETTINGS;
  });

  // Role configs state
  const [savedRoleConfigs, setSavedRoleConfigs] = useState<ProjectRoleConfig[]>(() =>
    parseRoleConfigs(localStorage.getItem(ROLE_CONFIGS_STORAGE_KEY))
  );

  // Current working state (may have unsaved changes)
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>(savedFieldConfigs);
  const [timelineSettings, setTimelineSettings] = useState<TimelineSettings>(savedTimelineSettings);
  const [defaultStatusId, setDefaultStatusId] = useState<number | null>(savedDefaultStatusId);
  const [projectCodeSettings, setProjectCodeSettings] = useState<ProjectCodeSettings>(savedProjectCodeSettings);
  const [roleConfigs, setRoleConfigs] = useState<ProjectRoleConfig[]>(savedRoleConfigs);

  // Sync Start Date requirement with timeline completion setting
  useEffect(() => {
    setFieldConfigs(prevConfigs => 
      prevConfigs.map(config => {
        if (config.key !== 'startDate') return config;
        const newFieldState: FieldState = timelineSettings.timelineCompletionRequired ? 'required' : 'optional';
        return { ...config, fieldState: newFieldState, isRequired: deriveIsRequired(newFieldState) };
      })
    );
  }, [timelineSettings.timelineCompletionRequired]);

  // Fetch settings from API on mount (in live mode)
  useEffect(() => {
    const loadSettingsFromApi = async () => {
      if (hasFetchedFromApi.current) return;
      hasFetchedFromApi.current = true;

      try {
        const apiSettings = await fetchProjectCreationSettings();
        if (apiSettings) {
          // API returned settings - use them and update localStorage
          const apiFieldConfigs = parseFieldConfigs(apiSettings.fieldConfigs);
          const apiTimelineSettings = parseTimelineSettings(apiSettings.timelineSettings);
          const apiDefaultStatusId = apiSettings.defaultStatusId ?? 2; // Default to "In Progress"

          setSavedFieldConfigs(apiFieldConfigs);
          setSavedTimelineSettings(apiTimelineSettings);
          setSavedDefaultStatusId(apiDefaultStatusId);
          setFieldConfigs(apiFieldConfigs);
          setTimelineSettings(apiTimelineSettings);
          setDefaultStatusId(apiDefaultStatusId);

          // Update localStorage to keep it in sync
          if (apiSettings.fieldConfigs) {
            localStorage.setItem(STORAGE_KEY, apiSettings.fieldConfigs);
          }
          if (apiSettings.timelineSettings) {
            localStorage.setItem(TIMELINE_STORAGE_KEY, apiSettings.timelineSettings);
          }
          if (apiDefaultStatusId !== null) {
            localStorage.setItem(DEFAULT_STATUS_STORAGE_KEY, String(apiDefaultStatusId));
          } else {
            localStorage.removeItem(DEFAULT_STATUS_STORAGE_KEY);
          }
        }
        // If API returns null (demo mode or no settings), keep localStorage values
      } catch (error) {
        console.error('Failed to load project creation settings from API:', error);
        setIsSettingsLoadError(true);
        // Keep localStorage values on error
      } finally {
        setIsLoading(false);
      }
    };

    loadSettingsFromApi();
  }, []);

  // Calculate hasUnsavedChanges by comparing current state with saved state
  const hasUnsavedChanges = 
    JSON.stringify(fieldConfigs) !== JSON.stringify(savedFieldConfigs) ||
    JSON.stringify(timelineSettings) !== JSON.stringify(savedTimelineSettings) ||
    JSON.stringify(projectCodeSettings) !== JSON.stringify(savedProjectCodeSettings) ||
    JSON.stringify(roleConfigs) !== JSON.stringify(savedRoleConfigs) ||
    defaultStatusId !== savedDefaultStatusId;

  const requiredFields = useMemo(
    () => fieldConfigs.filter(config => config.isRequired).map(config => config.key),
    [fieldConfigs],
  );

  const updateFieldConfig = useCallback((key: ProjectFieldKey, updates: Partial<FieldConfig>) => {
    setFieldConfigs(prev =>
      prev.map(config => {
        if (config.key === key) {
          // Prevent changing required status for always required fields
          if (config.isAlwaysRequired && ('isRequired' in updates || 'fieldState' in updates)) {
            return config;
          }
          const merged = { ...config, ...updates };
          // Keep isRequired in sync with fieldState
          if ('fieldState' in updates && updates.fieldState !== undefined) {
            merged.isRequired = deriveIsRequired(updates.fieldState);
          } else if ('isRequired' in updates && !('fieldState' in updates)) {
            merged.fieldState = migrateToFieldState(merged.isRequired);
          }
          return merged;
        }
        return config;
      })
    );
  }, []);

  const updateFieldState = useCallback((key: ProjectFieldKey, state: FieldState) => {
    setFieldConfigs(prev =>
      prev.map(config => {
        if (config.key !== key) return config;
        if (config.isAlwaysRequired) return config; // locked — cannot change
        return { ...config, fieldState: state, isRequired: deriveIsRequired(state) };
      })
    );
  }, []);

  const updateTimelineSettings = useCallback((updates: Partial<TimelineSettings>) => {
    setTimelineSettings(prev => {
      const newSettings = { ...prev, ...updates };
      
      // When timelineCompletionRequired changes, update Start Date requirement
      if ('timelineCompletionRequired' in updates) {
        setFieldConfigs(prevConfigs => 
          prevConfigs.map(config => 
            config.key === 'startDate' 
              ? { ...config, isRequired: newSettings.timelineCompletionRequired }
              : config
          )
        );
      }
      
      return newSettings;
    });
  }, []);

  const updateDefaultStatusId = useCallback((id: number | null) => {
    setDefaultStatusId(id);
  }, []);

  const updateProjectCodeSettings = useCallback((updates: Partial<ProjectCodeSettings>) => {
    setProjectCodeSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const updateRoleState = useCallback((role: ProjectRoleType, state: RoleState) => {
    // Project Manager is always required — cannot be changed
    if (role === 'project-manager') return;
    setRoleConfigs(prev =>
      prev.map((c) => (c.role === role ? roleStateToConfig(role, state) : c))
    );
  }, []);

  const generateExampleCode = useCallback((): string => {
    const { prefix, numberFormat, nextSequence } = projectCodeSettings;
    const padLength = numberFormat.length; // '001' -> 3, '0001' -> 4, '00001' -> 5
    const paddedNum = String(nextSequence).padStart(padLength, '0');
    return `${prefix}${paddedNum}`;
  }, [projectCodeSettings]);

  const getRequiredFieldsBySection = useCallback((section: FieldConfig['section']) => {
    return fieldConfigs
      .filter(config => config.isRequired && config.section === section)
      .sort((a, b) => a.order - b.order);
  }, [fieldConfigs]);

  const getFieldConfig = useCallback((key: ProjectFieldKey) => {
    return fieldConfigs.find(config => config.key === key);
  }, [fieldConfigs]);

  const generateProjectCode = useCallback((projectName: string, maxLength?: number): string => {
    if (!projectName) return '';
    
    // Convert to uppercase and remove special characters
    const cleaned = projectName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .trim();
    
    // Split into words and take first letter of each word (up to 3 words)
    const words = cleaned.split(/\s+/).filter(word => word.length > 0);
    const acronym = words.slice(0, 3).map(word => word[0]).join('');
    
    // Get current year
    const year = new Date().getFullYear();
    
    // Generate a simple sequential number (in real implementation, this would check for duplicates)
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    const code = `${acronym}-${year}-${sequence}`;
    
    // Respect the configured max length for project codes
    if (maxLength && code.length > maxLength) {
      return code.slice(0, maxLength);
    }
    
    return code;
  }, []);

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const fieldConfigsJson = JSON.stringify(fieldConfigs);
      const timelineSettingsJson = JSON.stringify(timelineSettings);
      const projectCodeSettingsJson = JSON.stringify(projectCodeSettings);
      const roleConfigsJson = JSON.stringify(roleConfigs);

      // Save to localStorage first for immediate feedback
      localStorage.setItem(STORAGE_KEY, fieldConfigsJson);
      localStorage.setItem(TIMELINE_STORAGE_KEY, timelineSettingsJson);
      localStorage.setItem(PROJECT_CODE_SETTINGS_KEY, projectCodeSettingsJson);
      localStorage.setItem(ROLE_CONFIGS_STORAGE_KEY, roleConfigsJson);
      if (defaultStatusId !== null) {
        localStorage.setItem(DEFAULT_STATUS_STORAGE_KEY, String(defaultStatusId));
      } else {
        localStorage.removeItem(DEFAULT_STATUS_STORAGE_KEY);
      }

      // Fetch current settings to preserve fieldLengthConstraints
      const currentSettings = await fetchProjectCreationSettings();

      // Save to API (in live mode), preserving existing fieldLengthConstraints
      await saveProjectCreationSettings({
        fieldConfigs: fieldConfigsJson,
        timelineSettings: timelineSettingsJson,
        fieldLengthConstraints: currentSettings?.fieldLengthConstraints ?? null,
        defaultStatusId,
      });

      setSavedFieldConfigs(fieldConfigs);
      setSavedTimelineSettings(timelineSettings);
      setSavedDefaultStatusId(defaultStatusId);
      setSavedProjectCodeSettings(projectCodeSettings);
      setSavedRoleConfigs(roleConfigs);
    } catch (error) {
      console.error('Failed to save project creation settings:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [fieldConfigs, timelineSettings, defaultStatusId, projectCodeSettings, roleConfigs]);

  const resetChanges = useCallback(() => {
    setFieldConfigs(savedFieldConfigs);
    setTimelineSettings(savedTimelineSettings);
    setDefaultStatusId(savedDefaultStatusId);
    setProjectCodeSettings(savedProjectCodeSettings);
    setRoleConfigs(savedRoleConfigs);
  }, [savedFieldConfigs, savedTimelineSettings, savedDefaultStatusId, savedProjectCodeSettings, savedRoleConfigs]);

  const resetToDefaults = useCallback(() => {
    setFieldConfigs(DEFAULT_FIELD_CONFIGS);
    setTimelineSettings(DEFAULT_TIMELINE_SETTINGS);
    setDefaultStatusId(null);
    setProjectCodeSettings(DEFAULT_PROJECT_CODE_SETTINGS);
    setRoleConfigs(DEFAULT_ROLE_CONFIGS);
  }, []);

  const makeAllRequired = useCallback(() => {
    setFieldConfigs(prev =>
      prev.map(config => ({ ...config, fieldState: 'required' as FieldState, isRequired: true }))
    );
    setRoleConfigs(prev =>
      prev.map(c => c.role === 'project-manager' ? c : roleStateToConfig(c.role, 'required'))
    );
  }, []);

  const makeAllOptional = useCallback(() => {
    setFieldConfigs(prev =>
      prev.map(config =>
        // Keep always required fields locked
        config.isAlwaysRequired
          ? config
          : { ...config, fieldState: 'optional' as FieldState, isRequired: false }
      )
    );
    setRoleConfigs(prev =>
      prev.map(c => c.role === 'project-manager' ? c : roleStateToConfig(c.role, 'optional'))
    );
  }, []);

  const makeAllHidden = useCallback(() => {
    setFieldConfigs(prev =>
      prev.map(config =>
        // Keep always required fields locked
        config.isAlwaysRequired
          ? config
          : { ...config, fieldState: 'hidden' as FieldState, isRequired: false }
      )
    );
    setRoleConfigs(prev =>
      prev.map(c => c.role === 'project-manager' ? c : roleStateToConfig(c.role, 'hidden'))
    );
  }, []);

  return (
    <FieldConfigContext.Provider
      value={{
        fieldConfigs,
        requiredFields,
        timelineSettings,
        defaultStatusId,
        projectCodeSettings,
        roleConfigs,
        updateFieldConfig,
        updateFieldState,
        updateTimelineSettings,
        updateDefaultStatusId,
        updateProjectCodeSettings,
        updateRoleState,
        getRequiredFieldsBySection,
        getFieldConfig,
        generateProjectCode,
        generateExampleCode,
        hasUnsavedChanges,
        isSaving,
        isLoading,
        isSettingsLoadError,
        saveChanges,
        resetChanges,
        resetToDefaults,
        makeAllRequired,
        makeAllOptional,
        makeAllHidden,
      }}
    >
      {children}
    </FieldConfigContext.Provider>
  );
}

export function useFieldConfig() {
  const context = useContext(FieldConfigContext);
  if (context === undefined) {
    throw new Error('useFieldConfig must be used within a FieldConfigProvider');
  }
  return context;
}
